import * as d3 from 'd3'
import throttle from 'lodash.throttle'
import { Component, MouseEvent, createRef } from 'react'

import { isFirefoxDesktop, isMobile } from '../shared/isMobile'
import { retinafy } from '../shared/retinafy'
import BubbleActionCreators from './actions/bubbleActionCreators'
import BubblesHoverCanvas from './bubblesHoverCanvas'
import { BubbleSettings } from './constants/settings'
import Legend from './legend'
import * as Selects from './selects'
import Slider from './slider'
import BubbleStore from './stores/bubbleStore'
import HoverStore from './stores/hoverStore'
import { BubbleNode, Range } from './types'
import { getScales, getXScale, getYScale } from './utils'

const BUBBLES_MIN_WIDTH = 794
const SIDEBAR_WIDTH = 200
const TOOLTIP_WIDTH = 60
const SLIDER_WIDTH = 24

interface SelectOption {
  value: string
  label: string
  format?: string
}

var labelX: SelectOption, labelY: SelectOption // TODO

interface BubbleProps {
  settings: BubbleSettings
}

interface BubbleChartState {
  bubbles: any
  errorCode: any
  hasError: any
  isLoading: any
  params: any
  scales: any
  width: number
  height: number
  hoveredColor: string | null
}

class BubbleChart extends Component<BubbleProps, BubbleChartState> {
  canvasRef = createRef<HTMLCanvasElement>()
  declare canvasContext: CanvasRenderingContext2D

  constructor(props: any) {
    super(props)

    this.state = this._getState()
  }

  componentDidMount() {
    BubbleStore.addChangeListener(this._updateState)
    HoverStore.addChangeListener(this._onHoverStoreChange)
    const canvas = this.canvasRef.current
    const context = canvas?.getContext('2d', isFirefoxDesktop() ? { willReadFrequently: true } : undefined)

    if (!canvas || !context) return

    this.canvasContext = context

    Selects.xys.forEach((option) => {
      if (option.value === this.state.params.x) {
        labelX = option
      }
      if (option.value === this.state.params.y) {
        labelY = option
      }
    })

    if (isMobile()) {
      window.addEventListener('orientationchange', this._onResize)
    } else {
      window.addEventListener('resize', this._onResize)
    }
  }

  componentWillUnmount() {
    BubbleStore.removeChangeListener(this._updateState)
    HoverStore.removeChangeListener(this._onHoverStoreChange)
    window.removeEventListener('resize', this._onResize)
    window.removeEventListener('orientationchange', this._onResize)
  }

  _onResize = throttle(() => {
    const size = this._getSize()
    if (size.width !== this.state.width && size.height !== this.state.height) {
      this._updateState()
    }
  }, 100)

  _updateState = () => {
    this.setState(this._getState())
  }

  _getSize() {
    const browserWidth = Math.max(document.documentElement.clientWidth, window.innerWidth || 0)
    const browserHeight = Math.max(document.documentElement.clientHeight, window.innerHeight || 0)
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
    const isScreener = document.body.classList.contains('is-screener')
    const container = document.getElementById(isScreener ? 'root' : 'bubbles')
    const containerWidth = (container?.clientWidth ?? BUBBLES_MIN_WIDTH) - scrollbarWidth
    const spacing = this.props.settings.margin.left + this.props.settings.margin.right + SIDEBAR_WIDTH
    let width = containerWidth - spacing

    if (browserWidth - containerWidth < TOOLTIP_WIDTH) {
      width = width - TOOLTIP_WIDTH / 2
    }

    if (browserWidth - containerWidth - TOOLTIP_WIDTH > TOOLTIP_WIDTH) {
      width = width + SLIDER_WIDTH
    }

    const ratio =
      (browserWidth <= 1650 && browserHeight < 880) || (browserWidth >= 1650 && browserHeight < 980) ? 2 : 1.85
    const height = ~~(width / ratio)

    return { width, height }
  }

  _addShowTextToBubbles(bubbles: BubbleNode[]) {
    for (let i = 0; i < bubbles.length; i++) {
      const d = bubbles[i]
      const px = d.sx,
        py = d.sy,
        pr = d.sr + (d.sr > 10 ? 0 : 15),
        ax1 = px - pr,
        ax2 = px + pr,
        ay1 = py - pr,
        ay2 = py + pr

      d.showText = { px, py }

      for (let j = 0; j < bubbles.length; j++) {
        const d2 = bubbles[j]
        // eslint-disable-next-line eqeqeq
        if (d == d2) {
          continue
        }
        const bpx = d2.sx,
          bpy = d2.sy,
          bpr = d2.sr,
          bx1 = bpx - bpr,
          bx2 = bpx + bpr,
          by1 = bpy - bpr,
          by2 = bpy + bpr
        if (!(ax1 > bx2 || bx1 > ax2 || ay1 > by2 || by1 > ay2)) {
          d.showText = false
          break
        }
      }
    }
  }

  _getState = () => {
    // precompute scaled x, y, size
    const bubblesInRange = BubbleStore.getBubblesInRange()
    const params = BubbleStore.getParams()
    const size = this._getSize()
    const scales = getScales(bubblesInRange, size.width, size.height, params.color, this.props.settings.colorPalette)
    const hoveredColor = HoverStore.getHoveredColor()

    const bubbles = bubblesInRange.map((node) => ({
      ...node,
      sx: Math.round(scales.x(node.x)),
      sy: Math.round(scales.y(node.y)),
      sr: Math.max(Math.round(scales.r(node.size)), 1.5),
      sc: scales.c(node.color),
    }))
    this._addShowTextToBubbles(bubbles)

    return {
      bubbles,
      params,
      scales,
      isLoading: BubbleStore.isLoading(),
      hasError: BubbleStore.hasError(),
      errorCode: BubbleStore.getErrorCode(),
      width: size.width,
      height: size.height,
      hoveredColor,
    }
  }

  _onHoverStoreChange = () => {
    const hoveredColor = HoverStore.getHoveredColor()
    if (hoveredColor !== this.state.hoveredColor) {
      this.setState({ hoveredColor })
    }
  }

  render() {
    const { width, height } = this.state
    const { margin } = this.props.settings

    var message
    if (this.state.isLoading) {
      message = 'Loading...'
    } else if (this.state.hasError) {
      message = 'Oops, something went wrong. Please refresh the page or try again.'
      if (this.state.errorCode === 400) {
        message = 'Page not found.'
      }
    } else if (!this.state.isLoading && !this.state.hasError && this.state.bubbles.length === 0) {
      message = 'No stocks selected.'
    }

    var { c } = this.state.scales
    var allBubbles = BubbleStore.getBubbles()

    var formatX = Selects.xys.filter((o) => o.value === this.state.params.x)[0].format
    var formatY = Selects.xys.filter((o) => o.value === this.state.params.y)[0].format

    return (
      <div className="mt-3 select-none">
        <canvas
          ref={this.canvasRef}
          width={width + margin.left + margin.right}
          height={height + margin.top + margin.bottom}
          id="bubble-chart-canvas"
        >
          {document.title}
        </canvas>
        <BubblesHoverCanvas
          key={width + height}
          width={width}
          height={height}
          margin={margin}
          bubbles={this.state.bubbles}
          onDoubleTap={this._onDoubleTap}
          settings={this.props.settings}
        />

        {!this.state.isLoading && !this.state.hasError && this.state.bubbles.length >= 0 && (
          <div>
            <div style={{ width: width + margin.left + 2 }}>
              <Legend colorFn={c} />
            </div>

            <div style={{ marginLeft: margin.left + 1, position: 'absolute', top: -7 }}>
              {/* @ts-ignore */}
              <Slider
                key={width}
                bubbles={allBubbles}
                range={BubbleStore.getRange('x')}
                scale={getXScale(allBubbles, width)}
                format={formatX}
                width={width}
                onRangeChange={this._onRangeChange.bind(this, 'x')}
                settings={this.props.settings}
              />
            </div>

            <div style={{ position: 'absolute', top: margin.top, marginLeft: margin.left + width + 13 }}>
              {/* @ts-ignore */}
              <Slider
                key={height}
                bubbles={allBubbles}
                range={BubbleStore.getRange('y')}
                scale={getYScale(allBubbles, height)}
                format={formatY}
                type="vertical"
                height={height}
                onRangeChange={this._onRangeChange.bind(this, 'y')}
                settings={this.props.settings}
              />
            </div>
          </div>
        )}

        {message && <p style={{ color: '#707990', position: 'absolute', top: 9, left: 90, margin: 0 }}>{message}</p>}
      </div>
    )
  }

  componentDidUpdate() {
    this._render()
  }

  _render = () => {
    const { width, height } = this.state
    if (this.state.isLoading || this.state.hasError) {
      return
    }

    var context = this.canvasContext,
      data = this.state.bubbles
    const { settings } = this.props

    var { x, y } = this.state.scales

    retinafy(
      context.canvas,
      context,
      width + settings.margin.left + settings.margin.right,
      height + settings.margin.top + settings.margin.bottom
    )

    context.save()
    context.lineWidth = 1
    context.fillStyle = settings.background
    context.fillRect(
      0,
      0,
      width + settings.margin.left + settings.margin.right,
      height + settings.margin.top + settings.margin.bottom
    )
    context.translate(settings.margin.left, settings.margin.top)

    context.strokeStyle = settings.grid
    var xTicks = x.ticks(10)
    context.beginPath()
    for (var i = 0; i < xTicks.length; i++) {
      var tick = xTicks[i]
      var dx = Math.round(x(tick)) + 0.5
      context.moveTo(dx, 0)
      context.lineTo(dx, height)
    }
    context.stroke()

    var yTicks = y.ticks(10)
    context.beginPath()
    for (i = 0; i < yTicks.length; i++) {
      tick = yTicks[i]
      var dy = Math.round(y(tick)) + 0.5
      context.moveTo(0.5, dy)
      context.lineTo(width + 0.5, dy)
    }
    context.stroke()

    context.strokeStyle = settings.axis.stroke
    context.beginPath()
    dx = Math.round(x(x.domain()[0])) + 0.5
    context.moveTo(dx, 0)
    context.lineTo(dx, height)
    for (i = 0; i < xTicks.length; i++) {
      tick = xTicks[i]
      dx = Math.round(x(tick)) + 0.5
      context.moveTo(dx, height)
      context.lineTo(dx, height + 6.5)
    }
    context.stroke()

    context.beginPath()
    dy = Math.round(y(y.domain()[0])) + 0.5
    context.moveTo(0.5, dy)
    context.lineTo(width + 0.5, dy)
    for (i = 0; i < yTicks.length; i++) {
      tick = yTicks[i]
      dy = Math.round(y(tick)) + 0.5
      context.moveTo(0.5, dy)
      context.lineTo(-6.5, dy)
    }
    context.stroke()

    context.font = '8pt Verdana'
    context.fillStyle = settings.axis.text
    context.textAlign = 'center'
    context.textBaseline = 'bottom'
    for (i = 0; i < xTicks.length; i++) {
      tick = xTicks[i]
      dx = Math.round(x(tick)) + 0.5
      context.fillText(formatXAxis(tick), dx, height + 22)
    }

    context.textAlign = 'right'
    context.textBaseline = 'middle'
    for (i = 0; i < yTicks.length; i++) {
      tick = yTicks[i]
      dy = Math.round(y(tick)) + 0.5
      context.fillText(formatYAxis(tick), -12, dy)
    }

    context.fillText(labelX.label, width - 1, height - 10)

    context.textAlign = 'right'
    context.save()
    context.translate(10, 1)
    context.rotate((-90 * Math.PI) / 180)
    context.fillText(labelY.label, 0, 0)
    context.restore()

    context.lineWidth = 1.1
    context.textAlign = 'center'
    context.textBaseline = 'middle'
    for (i = 0; i < data.length; i++) {
      var d = data[i]

      var clr = d.sc
      var isBubbleDimmed = !!this.state.hoveredColor && this.state.hoveredColor !== d.color
      var rgb, fillStyle, strokeStyle

      if (isBubbleDimmed) {
        fillStyle = settings.bubble.fill
        strokeStyle = settings.bubble.stroke
      } else {
        rgb = d3.rgb(clr)
        fillStyle = 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + 0.85 + ')'
        rgb.r = Math.round(rgb.r * 0.9 + 255 * 0.15)
        rgb.g = Math.round(rgb.g * 0.9 + 255 * 0.15)
        rgb.b = Math.round(rgb.b * 0.9 + 255 * 0.15)
        strokeStyle = 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + 1 + ')'
      }

      context.beginPath()
      context.arc(d.sx, d.sy, d.sr, 0, 2 * Math.PI, true)
      context.fillStyle = fillStyle
      context.fill()
      context.strokeStyle = strokeStyle
      context.stroke()

      this._renderText(context, d, clr, strokeStyle, settings, isBubbleDimmed)
    }
    context.restore()
  }

  _renderText = (
    context: CanvasRenderingContext2D,
    d: BubbleNode,
    clr: string,
    strokeStyle: string,
    settings: BubbleSettings,
    isBubbleDimmed: boolean
  ) => {
    if (d.showText) {
      const { px, py } = d.showText
      if (d.sr > 10) {
        if (!isBubbleDimmed) {
          var rgb = d3.rgb(clr)
          rgb.r = Math.round(rgb.r * 0.9 + 255 * 0.55)
          rgb.g = Math.round(rgb.g * 0.9 + 255 * 0.55)
          rgb.b = Math.round(rgb.b * 0.9 + 255 * 0.55)
          strokeStyle = 'rgba(' + rgb.r + ',' + rgb.g + ',' + rgb.b + ',' + 1 + ')'
        }

        context.font = '12px Lato'
        if (settings.bubble.textShadow) {
          context.fillStyle = settings.bubble.textShadow
          context.fillText(d.ticker, px + 1, py + 1)
        }
        context.fillStyle = settings.bubble.color ?? strokeStyle
        context.fillText(d.ticker, px, py)
      } else {
        context.font = '11px Lato'
        if (settings.textShadow) {
          context.fillStyle = settings.textShadow
          context.fillText(d.ticker, px + 1, py + d.sr + 11)
        }
        context.fillStyle = settings.color ?? strokeStyle
        context.fillText(d.ticker, px, py + d.sr + 10)
      }
    }
  }

  _onDoubleTap = (e: HammerInput) => {
    e.preventDefault()
    const hovered = this._getHoveredNode(e.pointers[0])
    if (hovered) {
      window.open('/quote.ashx?t=' + hovered.ticker)
    }
  }

  _onRangeChange = (type: string, range: Range) => {
    BubbleActionCreators.changeRange(type, range.scaled!)
  }

  _getHoveredNode = (e: MouseEvent) => {
    var data = this.state.bubbles
    var p = this._getArea(e)
    p.x -= this.props.settings.margin.left
    p.y -= this.props.settings.margin.top
    var hovered
    for (var i = data.length - 1; i >= 0; i--) {
      var d = data[i]
      if (Math.pow(p.x - d.sx, 2) + Math.pow(p.y - d.sy, 2) < Math.pow(d.sr, 2)) {
        hovered = d
        break
      }
    }
    return hovered
  }

  _getArea = (e: MouseEvent) => {
    // @ts-ignore
    var rect = e.target.getBoundingClientRect()
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    }
  }
}

var format = d3.format('~s')
function formatXAxis(value: number) {
  var formatted: string | number = parseFloat(value.toFixed(2))
  if (value > 1 || value < -1) {
    formatted = format(formatted).replace('G', 'B')
  }
  if (labelX.format === '%') {
    formatted += '%'
  }
  return formatted as string
}

function formatYAxis(value: number) {
  var formatted: string | number = parseFloat(value.toFixed(2))
  if (value > 1 || value < -1) {
    formatted = format(formatted).replace('G', 'B')
  }
  if (labelY.format === '%') {
    formatted += '%'
  }
  return formatted as string
}

export { formatXAxis, formatYAxis, BubbleChart }
